// @ vendor
const { isEmpty } = require('lodash');

// @ commons
const { APIGet, APIPost } = require('utilities/APIRequestHelper');
const { scrollToTop } = require('commonsUtilities/scrollUtils');
const { addAcceptedText } = require('../../insuranceUtilities/addAcceptedTextHelper');

// @ actions
const { requestInsuranceRelatedRNCCurrentState } = require('./insuranceRequestRelatedRNCState');
const { requestInsuranceCurrentPricing, linkClient, linkClientSuccessInitialState, basicPlanAcceptanceModal } = require('./insuranceRequestRelatedRNCWizardStep3');
const { getDocument, sendBudgetEmail } = require('./insuranceRequestRelatedRNCWizardStep5');
const { modifyInsurance } = require('../insuranceRequestRelated/insuranceRequestRelatedModify');

// @ constants
const { 
    WIZARD_REQUEST_RESPONSE_OPTIONS,
    PLANS: { BASIC } 
} = require('../../insuranceConstants/insuranceRequestHome');

const {
    FIRST_STEP,
    SECOND_STEP,
    THIRD_STEP,
    FOURTH_STEP
} = require('constants/index');
const {
    INSURANCE_REQUEST_CHANGE_ERROR_POSTAL_CODE,
    INSURANCE_REQUEST_CHANGE_ERROR_POPULATION,
    INSURANCE_REQUEST_CLEAR_POSTAL_CODE_ERROR,
    INSURANCE_REQUEST_RELATED_RNC_WIZARD_LOAD_STEP,
    INSURANCE_REQUEST_RELATED_RNC_WIZARD_RESET_STEP,
    INSURANCE_REQUEST_RELATED_RNC_WIZARD_VALIDATE_STEP,
    INSURANCE_REQUEST_RELATED_RNC_WIZARD_SET_VISIBLE_STEP,
    INSURANCE_REQUEST_RELATED_RNC_WIZARD_SET_DATA,
    INSURANCE_REQUEST_RELATED_RNC_WIZARD_WILL_CANCEL,
    INSURANCE_REQUEST_RELATED_RNC_POST_WIZARD_SUCCESS,
    INSURANCE_REQUEST_RELATED_RNC_CANCEL_SIMULATION_REQUEST,
    INSURANCE_REQUEST_RELATED_RNC_CANCEL_SIMULATION_SUCCESS,
    INSURANCE_REQUEST_RELATED_RNC_CANCEL_SIMULATION_FAILURE,
    INSURANCE_REQUEST_RELATED_RNC_POST_APPRAISAL_WIZARD_SET_DATA,
    INSURANCE_REQUEST_RELATED_RNC_SET_MIN_CONTINENT_VALUE,
    INSURANCE_REQUEST_SELECT_POPULATION_REQUEST,
    INSURANCE_REQUEST_SELECT_POPULATION_FAILURE,
    INSURANCE_REQUEST_SELECT_POPULATION_SUCCESS,
} = require('../../insuranceConstants/actionTypes');
const {
    INSURANCE_RELATED_LEGAL_CHECK,
    INSURANCE_USABILITY_IMPROVEMENTS,
    PRE_CONTRACTUAL_RELATED_INSURANCE_MAIL,
} = require('constants/flagBasedFeatures');

const changeErrorPostalCode = () => ({
    type: INSURANCE_REQUEST_CHANGE_ERROR_POSTAL_CODE
});

const changeErrorPopulation = () => ({
    type: INSURANCE_REQUEST_CHANGE_ERROR_POPULATION
});

const clearPostalCodeError = () => ({
    type: INSURANCE_REQUEST_CLEAR_POSTAL_CODE_ERROR,
});

const loadStep = (step, data) => ({
    type: INSURANCE_REQUEST_RELATED_RNC_WIZARD_LOAD_STEP,
    data: {
        ...data,
        step
    }
});

const setVisibleStep = step => ({
    type: INSURANCE_REQUEST_RELATED_RNC_WIZARD_SET_VISIBLE_STEP,
    data: {
        step
    }
});

const validateWithUI = step => ({
    type: INSURANCE_REQUEST_RELATED_RNC_WIZARD_VALIDATE_STEP,
    data: {
        step
    }
});

const setWillCancel = willCancel => ({
    type: INSURANCE_REQUEST_RELATED_RNC_WIZARD_WILL_CANCEL,
    data: {
        willCancel
    }
});

const setData = (step, data) => ({
    type: INSURANCE_REQUEST_RELATED_RNC_WIZARD_SET_DATA,
    data: {
        step,
        ...data
    }
});

const setPostAppraisalInitialData = (step, data) => ({
    type: INSURANCE_REQUEST_RELATED_RNC_POST_APPRAISAL_WIZARD_SET_DATA,
    data: {
        step,
        ...data
    }
});

const cancelSimulationInProgress = () => ({
    type: INSURANCE_REQUEST_RELATED_RNC_CANCEL_SIMULATION_REQUEST
});

const cancelSimulationSuccess = id => ({
    type: INSURANCE_REQUEST_RELATED_RNC_CANCEL_SIMULATION_SUCCESS,
    data: { id }
});

const cancelSimulationFailure = error => ({
    type: INSURANCE_REQUEST_RELATED_RNC_CANCEL_SIMULATION_FAILURE,
    data: { error }
});

const wizardFinalRequest = response => ({
    type: INSURANCE_REQUEST_RELATED_RNC_POST_WIZARD_SUCCESS,
    data: { response }
});

const setMinContinentValue = minContinentValue => ({
    type: INSURANCE_REQUEST_RELATED_RNC_SET_MIN_CONTINENT_VALUE,
    data: { minContinentValue }
});

const requestSelectPopulationInProgress = () => ({
    type: INSURANCE_REQUEST_SELECT_POPULATION_REQUEST
});

const requestSelectPopulationSuccess = population => ({
    type: INSURANCE_REQUEST_SELECT_POPULATION_SUCCESS,
    data: {
        population
    }
});

const requestSelectPopulationFailure = error => ({
    type: INSURANCE_REQUEST_SELECT_POPULATION_FAILURE,
    data: { error }
});

const resetStep = step => ({
    type: INSURANCE_REQUEST_RELATED_RNC_WIZARD_RESET_STEP,
    data: {
        step
    }
});

const getPopulationData = (postalCode, ignoreFetching) => (dispatch, getState) => {
    const immStep2 = getState().insuranceRequestWizard.getIn(['steps', SECOND_STEP]);

    if (ignoreFetching || !immStep2.get('isFetchingSelectPopulation')) {
        dispatch(requestSelectPopulationInProgress());
        const params = {
            postalCode
        };

        return APIGet({
            dispatch,
            endpoint: 'insuranceRequestPopulation',
            params
        })
            .then((response) => {
                dispatch(requestSelectPopulationSuccess(response.data));
            })
            .catch(response => {
                if (!!response.error) {
                    dispatch(requestSelectPopulationFailure(response.error));
                } else {
                    throw response;
                }
            });
    } else {
        return Promise.resolve();
    }
};

const cancelSimulation = simulationToken => dispatch => {
    dispatch(cancelSimulationInProgress());

    return APIPost({
        dispatch,
        endpoint: 'insuranceRequestCancelQuotation'
    })
        .then(() => {
            dispatch(cancelSimulationSuccess());
            if (simulationToken) {
                dispatch(linkClient(simulationToken));
            } else {
                dispatch(requestInsuranceRelatedRNCCurrentState());
            }
        })
        .catch(response => {
            if (!!response.error) {
                dispatch(cancelSimulationFailure(response.error));
            } else {
                throw response;
            }
        });
};

const validateWizzardSuccess = () => (dispatch, getState) => {
    const prevInsuranceId = getState().insuranceRequestRelatedRNCWizard.getIn(['steps', FOURTH_STEP, 'prevInsuranceId']);
    const insuranceRequestModify = getState().insuranceRequestModify.get('insuranceIsBeingModified');
    const previousInsurance = insuranceRequestModify ? prevInsuranceId : 0;
    const currentState = getState().insuranceRequestRelatedRNCState.get('data');
    const immFlagBasedFeatures = getState().flagBasedFeatures;
    const insuranceId = currentState.get('id');
    const params = {
        query: {
            dataToken: null,
            insuranceId,
            previousInsurance
        }
    };
    if (insuranceRequestModify) {
        dispatch(modifyInsurance(false, true));
    }

    if (immFlagBasedFeatures.get(INSURANCE_RELATED_LEGAL_CHECK)) {
        params['query']['acceptedText'] = addAcceptedText();
    }

    dispatch(wizardFinalRequest(WIZARD_REQUEST_RESPONSE_OPTIONS.IS_FETCHING));
    return APIPost({
        dispatch,
        endpoint: 'insuranceRequestRelatedAuthorize',
        params
    })
        .then(() => {
            dispatch(wizardFinalRequest(WIZARD_REQUEST_RESPONSE_OPTIONS.SUCCESS));
        })
        .catch(() => {
            dispatch(wizardFinalRequest(WIZARD_REQUEST_RESPONSE_OPTIONS.ERROR));

        })
};

const validateStep = (step, basicPlanAccepted) => (dispatch, getState) => {
    const immFlagBasedFeatures = getState().flagBasedFeatures;
    const stepListNumber = step - 1;
    dispatch(validateWithUI(step));
    const immStep = getState().insuranceRequestRelatedRNCWizard.getIn(['steps', stepListNumber]);
    const isValidUI = immStep.get('validUI');
    const isBasicPlan = immStep.get('insurancePlan') === BASIC;
    const insuranceUsabilityImprovementsFlag = !!immFlagBasedFeatures.get(INSURANCE_USABILITY_IMPROVEMENTS);
    const selectPlanValidation = !insuranceUsabilityImprovementsFlag ?
        isValidUI && step === FOURTH_STEP :
        isValidUI && step === FOURTH_STEP && (isBasicPlan && basicPlanAccepted || !isBasicPlan);

    if (!isValidUI) {
        scrollToTop();
    }

    if(step === FOURTH_STEP && isBasicPlan && !basicPlanAccepted) {
        dispatch(basicPlanAcceptanceModal());
    }


    if (selectPlanValidation) {
        const insurancePlan = immStep.get('insurancePlan');
        const islinkingSuccess = immStep.get('islinkingSuccess');
        if (!islinkingSuccess) {
            dispatch(linkClient(insurancePlan));
        }
        dispatch(setMinContinentValue(0));
    }

    if (step === 4) {
        const isValid = immStep.get('valid');
        const isAcceptedConditions = immStep.get('acceptedConditions');
        if (isValid && isAcceptedConditions) {
            if(!immFlagBasedFeatures.get(PRE_CONTRACTUAL_RELATED_INSURANCE_MAIL)){
                dispatch(sendBudgetEmail());
            }
            dispatch(validateWizzardSuccess());
        }
    }
};

const loadSteps = data => dispatch => {
    if (!isEmpty(data)) {
        dispatch(loadStep(FIRST_STEP, data));
        dispatch(loadStep(SECOND_STEP, data));
        dispatch(loadStep(THIRD_STEP, data));
    }
};

module.exports = {
    cancelSimulation,
    changeErrorPostalCode,
    changeErrorPopulation,
    clearPostalCodeError,
    getDocument,
    getPopulationData,
    linkClientSuccessInitialState,
    loadSteps,
    requestInsuranceCurrentPricing,
    resetStep,
    setData,
    setPostAppraisalInitialData,
    setVisibleStep,
    setWillCancel,
    validateStep,
};
